/* eslint-disable  @typescript-eslint/no-non-null-assertion */
import { FC, ReactNode, useCallback, useMemo, useEffect, useRef, useState, Dispatch, SetStateAction } from 'react'
import { Box, ClickAwayListener, Skeleton, Typography } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community'
import { FilterChangedEvent } from 'ag-grid-enterprise'
import 'ui/components/records-grid/registerAgGrid'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { Link, useLocation, useNavigate } from 'react-router-dom'

/* Utils */
import axios, { isAxiosError } from 'lib/axios'
import { normalizeMetricStatus } from 'lib/metricStatus'
import {
  Application,
  ChartData,
  ColumnValue,
  CPISeverity,
  DEVICES_COLUMN_NAME,
  DevicesDataItem,
  DevicesQueryPagination,
  DevicesQueryPayload,
  ExclusionFieldsResponse,
  ExclusionRule,
  ExclusionRuleConditionInput,
  ExclusionRuleMatchType,
  MAX_CONDITION_VALUES,
  MAX_TICKET_RECORDS,
  MetricManifest,
  TrendsDataPayload
} from 'models'
import {
  createQueryPayload,
  generateColumns,
  formatDateValues,
  getDisplayFilterValue,
  blobToErrorContent,
  createActionsColumnDef,
  isDateRangeColumn,
  isFallbackRowId,
  ROW_ID_KEY,
  withRowIds
} from './utils'
import { useRecordsColumns } from 'ui/components/records-grid/useRecordsColumns'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setDevicesCount } from 'store/trends/trendsSlice'
import { formatCpiNameToDisplayName, hasPermission, Permission } from 'lib/utils'
import { ModalType, modalPropsSelector, openModal } from 'store/modals/modalSlice'
import { useAuth } from '@frontegg/react'
import { getPayloadTimePeriod } from '../../../library/utils'

/* Components */
import {
  DevicesTableContainer,
  ExclusionRulesButton,
  HeaderInfoWrapper,
  ManagementStatusWrapper,
  SelectionActionBar
} from './devices.styles'
import ControlButton from './components/ControlButton'
import { ThemeButton } from 'ui/components/buttons'
import ExclusionRulesView from '../exclusion-rules/ExclusionRulesView'
import ColumnPicker from './components/ColumnPicker'
import { DTPicker } from './DTPicker'
import DataDrawer from './components/DataDrawer'
import { LibraryDetailsHoverTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { ExclusionRulesServiceV4 } from 'services'
import {
  buildCreateRuleModalProps,
  CreateRuleMode
} from 'ui/components/modal/variants/exclusion-rule/createRuleModalProps'
import {
  convertFilterToConditions,
  selectionCondition,
  unsupportedFieldsMessage
} from 'ui/components/modal/variants/exclusion-rule/conditionBuilder'
import {
  EXCLUSION_FIELDS_UNAVAILABLE_MESSAGE,
  NO_EDIT_PERMISSION_MESSAGE,
  NO_RECORDS_TO_EXCLUDE_MESSAGE,
  TOO_MANY_RECORDS_MESSAGE
} from 'ui/components/modal/variants/exclusion-rule/exclusionRule.constants'
import {
  listTicketingApplications,
  OUTBOUND_INTEGRATIONS_PATH,
  TICKET_LIMIT_MESSAGE,
  TICKET_NO_PERMISSION_MESSAGE,
  TICKET_RECORDS_NOT_LOADED_MESSAGE
} from 'ui/components/modal/variants/create-ticket/createTicket.constants'
import { recordDateRange } from 'ui/components/modal/variants/create-ticket/jiraFields'
import { positionRecordsGridPopup } from 'ui/components/records-grid/gridPopup'
import LoadingRow from './components/LoadingRow'
import NoResultsFound from './components/NoResultsFound'
import FilterChipContainer from './components/FilterChipContainer'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import ErrorOverlay from './components/ErrorOverlay'
import { FilterLimitErrorModal } from './components/FilterLimitErrorModal'
import { BooleanFilter } from './components/BooleanFilter'
import { HotToast } from 'ui/components/toast'
import FilterLoadingOverlay from './components/FilterLoadingOverlay'

const DOWNLOAD_TIMEOUT_MS = 30000

const NO_FILTER_MESSAGE = 'Apply a filter to exclude all matching records, or select records individually.'
const PARTIAL_SELECT_ALL_MESSAGE =
  'A rule from a filter excludes every matching record. Select all records again, or select records individually.'

export interface DateFilterValue {
  dateFrom: string | null
  dateTo: string | null
  filterType: string
  type: string
}

// function instanceOfDateFilterValue(object: string[] | DateFilterValue): object is DateFilterValue {
//   return object && 'dateFrom' in object
// }

export interface ActiveFilter {
  filterName: string
  filterValue: string[] | DateFilterValue
}

export interface FilterLimitError {
  type: 'QUERY_METRIC_FILTER_LIMIT_ERROR'
  message: string
  context: {
    column_name: string
    actual_count: number
    limit: number
  }
}

interface Props {
  thisCpiName: string
  fetchQuery: ({ page, payload }: { page?: number; payload?: DevicesQueryPayload }) => Promise<any>
  customerName: string
  devicesPagination: DevicesQueryPagination
  columnValues: ColumnValue[]
  getLookup: (columnId: string) => Promise<string[]>
  setColumnValues: Dispatch<SetStateAction<ColumnValue[]>>
  setColumnsFetched: (value: boolean) => void
}

let savedFilterModel: any = null

const applyTrendsFilterModel = async (api: GridApi | null | undefined, model: Record<string, any> | null) => {
  if (!api || !model) return
  const keys = Object.keys(model)
  // setInitialModel() regenerates the grid columnDefs immediately before this runs, so the target
  // columns aren't mounted yet. Wait until at least one target column exists, then apply the whole
  // model in one setFilterModel() call (the high-level API applies + refetches and lets each set
  // filter lazy-load its own values), restricting to columns that actually exist in this schema.
  for (let attempt = 0; attempt < 40 && !keys.some((k) => api.getColumn(k)); attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 50))
  }
  const presentKeys = keys.filter((k) => api.getColumn(k))
  if (!presentKeys.length) return
  const presentModel = Object.fromEntries(presentKeys.map((k) => [k, model[k]]))
  api.setFilterModel(presentModel)
}

const Devices: FC<Props> = ({
  thisCpiName,
  fetchQuery,
  customerName,
  devicesPagination,
  setColumnValues,
  columnValues,
  getLookup,
  setColumnsFetched
}) => {
  const {
    chartData,
    cpi,
    selectedFrequency,
    selectedTimePeriod,
    selectedSeverity,
    manifestData,
    setChartData,
    selectedDataSourceIds,
    dataSourceOptions,
    setInitialQueryLoaded,
    metricManifest,
    snowflakeManifest,
    defaultRecentValueDate
  } = useLibraryDetails()
  const ref = useRef<AgGridReact>(null)
  // Scoping filters carried from the Trends tab ("View Off-SLA Entities"). Applied at the QUERY
  // level (merged into the records query in getRows) rather than via the AG Grid set filters —
  // programmatically setting a set filter's model leaves these custom async set filters unable to
  // render their value lists. Query-level application also reaches columns hidden by default
  // (e.g. ONYXIA_PRODUCT_INSTANCE) since createQueryPayload works off the manifest schema.
  const carriedFilterRef = useRef<Record<string, any> | null>(null)
  const dispatch = useAppDispatch()
  const { open: modalOpen } = useAppSelector(modalPropsSelector)
  // Set while a rule built from the selection is open; its dismissal drops the selection, as a save does.
  const ruleFromSelectionOpenRef = useRef(false)
  const { user: authUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const canEditExclusionRules = hasPermission(Permission.ExclusionRulesEdit, authUser as any)
  const canCreateTickets = hasPermission(Permission.TicketsCreate, authUser as any)
  // null while loading, so Create Ticket does not flash the no-integration state.
  const [ticketingApplications, setTicketingApplications] = useState<Application[] | null>(null)
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null)
  const [columnsPopperOpen, setColumnsPopperOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [devicesDataDrawerOpen, setDevicesDataDrawerOpen] = useState(false)
  const [activeRowItem, setActiveRowItem] = useState<DevicesDataItem | null>(null)
  const [initialFilterLoaded, setInitialFilterLoaded] = useState(false)
  const [gridApi, setGridApi] = useState<any>(null)
  const [showErrorOverlay, setShowErrorOverlay] = useState(false)
  const [cachedData, setCachedData] = useState<Map<string, DevicesDataItem[]>>(new Map())
  const [filterLimitError, setFilterLimitError] = useState<FilterLimitError | null>(null)
  const [lastValidActiveFilters, setLastValidActiveFilters] = useState<ActiveFilter[]>([])
  const [lastValidFilterModel, setLastValidFilterModel] = useState<any>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isHandlingError, setIsHandlingError] = useState(false)
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const [view, setView] = useState<'devices' | 'rules'>('devices')
  // Not every CPI has SK_ID (e.g. CPI-054, CPI-212); the manifest flags the field that identifies a record.
  const rowIdField = useMemo(
    () =>
      metricManifest?.druid_spec?.record_batch_spec?.input_schema?.find((s) => s.exclusionRecordIdentifier)?.name ??
      'SK_ID',
    [metricManifest]
  )
  const rowIdFieldRef = useRef(rowIdField)
  rowIdFieldRef.current = rowIdField
  // Tracks row ids (record identifier values) of selected rows. Sourced from AG Grid's SSRM selection state so the set
  // survives cache purges from filter changes (loaded vs. unloaded nodes alike).
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [rulesCount, setRulesCount] = useState<number | null>(null)
  // Whether the CPI holds any records at all, from the last unfiltered query. Null until one runs.
  const [hasRecords, setHasRecords] = useState<boolean | null>(null)
  // Header "select all" state: the selection is then the whole filtered set minus the deselected rows.
  const [selectAll, setSelectAll] = useState<{ active: boolean; deselected: number }>({
    active: false,
    deselected: 0
  })
  const [exclusionFields, setExclusionFields] = useState<ExclusionFieldsResponse | null>(null)
  const [filterRule, setFilterRule] = useState<{ conditions: ExclusionRuleConditionInput[]; blocked: string | null }>({
    conditions: [],
    blocked: null
  })
  const isFilterPopupOpen = useRef(false)

  // Fetched once per record-page load; every create entry point reads this response.
  useEffect(() => {
    if (!cpi) return
    let cancelled = false
    setExclusionFields(null)
    ExclusionRulesServiceV4.getExclusionFields(cpi.name)
      .then((res) => {
        if (!cancelled) setExclusionFields(res.data)
      })
      .catch((e) => console.error('Failed to load exclusion fields', e))
    return () => {
      cancelled = true
    }
  }, [cpi])

  useEffect(() => {
    if (!cpi) return
    let cancelled = false
    ExclusionRulesServiceV4.listRules(cpi.name)
      .then((res) => {
        if (cancelled) return
        setRulesCount((res.data.rules || []).length)
      })
      .catch(() => {
        if (!cancelled) setRulesCount(0)
      })
    return () => {
      cancelled = true
    }
  }, [cpi])

  const openCreateRuleModal = useCallback(
    (
      mode: CreateRuleMode,
      initialConditions: ExclusionRuleConditionInput[] = [],
      initialMatchType?: ExclusionRuleMatchType,
      onCreated?: () => void
    ) => {
      const createExclusionRuleProps = buildCreateRuleModalProps({
        cpi,
        exclusionFields,
        unitsLabel: snowflakeManifest?.units,
        dataSourceOptions,
        mode,
        initialConditions,
        initialMatchType,
        onSuccess: () => {
          ref.current?.api?.deselectAll()
          setSelectedIds([])
          setRulesCount((prev) => (prev === null ? 1 : prev + 1))
          setIsFilterLoading(false)
          onCreated?.()
        }
      })
      if (!createExclusionRuleProps) {
        toast.error(() => <HotToast text={EXCLUSION_FIELDS_UNAVAILABLE_MESSAGE} />)
        return
      }
      dispatch(openModal({ type: ModalType.createExclusionRule, props: { createExclusionRuleProps } }))
    },
    [cpi, exclusionFields, snowflakeManifest, dataSourceOptions, dispatch]
  )

  const openEditRuleModal = useCallback(
    (rule: ExclusionRule, onSaved: () => void) => {
      const createExclusionRuleProps = buildCreateRuleModalProps({
        cpi,
        exclusionFields,
        unitsLabel: snowflakeManifest?.units,
        dataSourceOptions,
        mode: 'edit',
        rule,
        onSuccess: onSaved
      })
      if (!createExclusionRuleProps) {
        toast.error(() => <HotToast text={EXCLUSION_FIELDS_UNAVAILABLE_MESSAGE} />)
        return
      }
      dispatch(openModal({ type: ModalType.createExclusionRule, props: { createExclusionRuleProps } }))
    },
    [cpi, exclusionFields, snowflakeManifest, dataSourceOptions, dispatch]
  )

  const handleExcludeRow = useCallback(
    (row: DevicesDataItem) => {
      if (!canEditExclusionRules || !exclusionFields) return
      const id = (row as any)[exclusionFields.record_identifier]
      if (id == null) return
      openCreateRuleModal('single', [selectionCondition(exclusionFields.record_identifier, [String(id)])])
    },
    [canEditExclusionRules, exclusionFields, openCreateRuleModal]
  )

  // Row ids are values of rowIdField, or a position when that value is missing or repeated; resolve
  // through the loaded row whenever the id is not itself the record identifier's value.
  const resolveRecordIds = useCallback(
    (rowIds: string[], recordIdentifier: string): string[] | null => {
      if (recordIdentifier === rowIdField && !rowIds.some(isFallbackRowId)) return rowIds
      const resolved = rowIds.map((id) => ref.current?.api?.getRowNode(id)?.data?.[recordIdentifier])
      return resolved.every((v) => v != null) ? Array.from(new Set(resolved.map(String))) : null
    },
    [rowIdField]
  )

  const handleCreateRuleFromSelection = useCallback(() => {
    if (!canEditExclusionRules) return
    if (selectAll.active) {
      if (filterRule.blocked || selectAll.deselected > 0) return
      ruleFromSelectionOpenRef.current = true
      openCreateRuleModal('filter', filterRule.conditions, ExclusionRuleMatchType.All)
      return
    }
    if (selectedIds.length === 0) return
    if (!exclusionFields) {
      toast.error(() => <HotToast text={EXCLUSION_FIELDS_UNAVAILABLE_MESSAGE} />)
      return
    }
    if (selectedIds.length > MAX_CONDITION_VALUES) {
      toast.error(() => <HotToast text={TOO_MANY_RECORDS_MESSAGE} />)
      return
    }
    const ids = resolveRecordIds(selectedIds, exclusionFields.record_identifier)
    if (!ids) {
      toast.error(() => <HotToast text="Some selected records are no longer loaded. Select them again." />)
      return
    }
    ruleFromSelectionOpenRef.current = true
    openCreateRuleModal('bulk', [selectionCondition(exclusionFields.record_identifier, ids)])
  }, [
    canEditExclusionRules,
    selectAll,
    filterRule,
    selectedIds,
    exclusionFields,
    resolveRecordIds,
    openCreateRuleModal
  ])

  useEffect(() => {
    if (modalOpen || !ruleFromSelectionOpenRef.current) return
    ruleFromSelectionOpenRef.current = false
    ref.current?.api?.deselectAll()
    setSelectedIds([])
  }, [modalOpen])

  useEffect(() => {
    let cancelled = false
    listTicketingApplications()
      .then((applications) => !cancelled && setTicketingApplications(applications))
      .catch(() => !cancelled && setTicketingApplications([]))
    return () => {
      cancelled = true
    }
  }, [])

  const noTicketingIntegration = ticketingApplications !== null && ticketingApplications.length === 0

  const openCreateTicketModal = useCallback(
    (rows: Record<string, any>[], ids: string[], onCreated?: () => void) => {
      if (!cpi || !ticketingApplications?.length) return
      // The Summary step shows the first data columns as laid out in the grid, skipping the source logo.
      const colDefs = (ref.current?.api?.getAllDisplayedColumns() ?? []).map((c) => c.getColDef())
      const recordColumns = colDefs
        .filter(
          (d) =>
            d.field &&
            d.headerName &&
            d.field !== 'actions' &&
            d.field !== DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE &&
            !d.field.endsWith('ONYXIA_PRODUCT_INSTANCE')
        )
        .slice(0, 3)
        .map((d) => ({ field: d.field as string, headerName: d.headerName as string }))
      dispatch(
        openModal({
          type: ModalType.createTicket,
          props: {
            createTicketProps: {
              cpiSlug: cpi.name,
              cpiDisplayId: cpi.display_name?.toUpperCase().replace(/\s+/g, '-') || cpi.name.toUpperCase(),
              applications: ticketingApplications,
              recordIds: ids,
              records: rows,
              recordColumns,
              targetDates: recordDateRange(rows, colDefs),
              onSuccess: onCreated
            }
          }
        })
      )
    },
    [cpi, ticketingApplications, dispatch]
  )

  const handleCreateTicketRow = useCallback(
    (row: DevicesDataItem) => {
      if (!canCreateTickets || noTicketingIntegration) return
      const id = (row as any).SK_ID
      if (id == null) return
      openCreateTicketModal([row], [String(id)])
    },
    [canCreateTickets, noTicketingIntegration, openCreateTicketModal]
  )

  const handleCreateTicketFromSelection = useCallback(() => {
    const api = ref.current?.api
    if (!api) return
    const rows: Record<string, any>[] = []
    if (selectAll.active) {
      api.forEachNode((node) => node.isSelected() && node.data && rows.push(node.data))
    } else {
      selectedIds.forEach((id) => {
        const data = api.getRowNode(id)?.data
        if (data) rows.push(data)
      })
    }
    const expected = selectAll.active ? Math.max(devicesPagination.total_results - selectAll.deselected, 0) : selectedIds.length
    if (!rows.length || rows.length !== expected) {
      toast.error(() => <HotToast text={TICKET_RECORDS_NOT_LOADED_MESSAGE} />)
      return
    }
    openCreateTicketModal(
      rows,
      rows.map((r) => String(r.SK_ID)),
      () => {
        api.deselectAll()
        setSelectedIds([])
      }
    )
  }, [selectAll, selectedIds, devicesPagination.total_results, openCreateTicketModal])

  // The grid keeps the colDef it was built with, so the cell calls through a ref to see late-loaded exclusion fields.
  const handleExcludeRowRef = useRef(handleExcludeRow)
  handleExcludeRowRef.current = handleExcludeRow
  const excludeRowFromCell = useCallback((row: DevicesDataItem) => handleExcludeRowRef.current(row), [])

  const handleCreateTicketRowRef = useRef(handleCreateTicketRow)
  handleCreateTicketRowRef.current = handleCreateTicketRow
  const createTicketFromCell = useCallback((row: DevicesDataItem) => handleCreateTicketRowRef.current(row), [])

  const noTicketingIntegrationMessage = useMemo(
    () => (
      <>
        No outbound integration is configured. Connect one in{' '}
        <Link to={OUTBOUND_INTEGRATIONS_PATH} style={{ color: 'inherit', textDecoration: 'underline' }}>
          Outbound Integrations
        </Link>
        .
      </>
    ),
    []
  )
  const rowTicketBlockedReason: ReactNode = !canCreateTickets
    ? TICKET_NO_PERMISSION_MESSAGE
    : noTicketingIntegration
    ? noTicketingIntegrationMessage
    : null
  const rowTicketBlockedReasonRef = useRef(rowTicketBlockedReason)
  rowTicketBlockedReasonRef.current = rowTicketBlockedReason
  const getRowTicketBlockedReason = useCallback(() => rowTicketBlockedReasonRef.current, [])

  // The integrations load after the grid renders, so the row buttons are redrawn once they arrive.
  useEffect(() => {
    ref.current?.api?.refreshCells({ columns: ['actions'], force: true })
  }, [canCreateTickets, noTicketingIntegration])

  const buildActionsColDef = useCallback(
    () =>
      createActionsColumnDef(dataSourceOptions, excludeRowFromCell, canEditExclusionRules, {
        onCreateTicket: createTicketFromCell,
        getBlockedReason: getRowTicketBlockedReason
      }),
    [dataSourceOptions, excludeRowFromCell, canEditExclusionRules, createTicketFromCell, getRowTicketBlockedReason]
  )

  const {
    columns,
    setColumns,
    colDefs,
    setColDefs,
    isUpdatingColumns,
    buildAllColDefs,
    handleChangeColumns,
    restoreDefaultColumns
  } = useRecordsColumns({
    gridRef: ref,
    initialColumns: generateColumns({ metricManifest }),
    columnValues,
    setColumnValues,
    getLookup,
    dataSourceOptions,
    buildTrailingColDef: buildActionsColDef
  })

  const checkForFilterLimitError = (error: any): FilterLimitError | null => {
    if (error?.response?.data?.error?.type === 'QUERY_METRIC_FILTER_LIMIT_ERROR') {
      return error.response.data.error
    }
    return null
  }

  useEffect(() => {
    return () => {
      setColumns([])
      setColDefs([])
      setActiveFilters([])
      setInitialFilterLoaded(false)
      savedFilterModel = null
      setCachedData(new Map())
      setLastValidActiveFilters([])
    }
  }, [thisCpiName])

  useEffect(() => {
    const abortController = new AbortController()
    return () => {
      abortController.abort()
    }
  }, [thisCpiName])


  const fetchTrendsData = useCallback(
    async (payload: TrendsDataPayload) => {
      if (!cpi || !cpi.configuration || !manifestData) return

      let severities: any = []
      const userInput = manifestData.ui.trend_chart.user_input
      if (userInput.formula_params[0]?.sections.length > 0) {
        severities = userInput.formula_params[0]?.sections
      } else if (userInput.visualization_param.sections.length > 1) {
        severities = userInput.visualization_param.sections
      }

      const newSection = severities.find((item: { display_name: CPISeverity }) => item.display_name === payload.section)
      if (newSection) payload.section = newSection.name

      try {
        const instanceNames = cpi
          ? cpi.configuration.integrations.filter((c) => !c.deleted).map((c) => c.instanceName)
          : []
        const payload = {
          metric_request: {
            section: selectedSeverity.toLowerCase(),
            frequency: selectedFrequency,
            instance_names: instanceNames,
            time_period: getPayloadTimePeriod(selectedTimePeriod)
          }
        }
        const res = await axios.post(`/api/v4/metrics/${cpi.name}/timeseries`, payload)

        if (res && res.status.toString().includes('20')) {
          const { data }: { data: ChartData } = res
          data.status = normalizeMetricStatus(data.status)
          const today = new Date().toISOString()
          const todayDate = today.slice(0, 10)
          const dataLastPoint = data.values[data.values.length - 1].xValue.slice(0, 10)
          if (dataLastPoint === todayDate) {
            data.values.pop()
          }

          const normalizedValues = data.values.map((value) => ({
            ...value,
            yValue: isNaN(value.yValue) ? 0 : value.yValue
          }))
          const normalizedData = {
            ...data,
            values: normalizedValues
          }

          setChartData({ ...normalizedData, status: data.status })

          return { ...normalizedData, status: data.status }
        }
      } catch (e) {
        console.error(e)
      }
    },
    [cpi, manifestData]
  )

  const setInitialModel = useCallback(
    async (metricManifest: MetricManifest, forceActiveColumns: string[] = []) => {
      try {
        // Force-activate columns that a carried filter targets (e.g. the hidden ONYXIA_PRODUCT_INSTANCE
        // / Instance Names column) so they're part of the grid and can hold a native set-filter.
        const force = new Set(forceActiveColumns)
        const newColumns = generateColumns({ metricManifest }).map((col) =>
          force.has(col.name) ? { ...col, active: true } : col
        )

        const newColDefs = await buildAllColDefs(newColumns)

        setColDefs(newColDefs)
        setActiveFilters([])
        savedFilterModel = null

        if (ref.current?.api) {
          ref.current.api.setFilterModel(null)
          ref.current.api.setGridOption('columnDefs', newColDefs)
        }

        localStorage.setItem('devices-filter-model', JSON.stringify(null))
        // Don't set initialFilterLoaded here - wait for first data load in onGridReady
      } catch (error) {
        // Don't set initialFilterLoaded here - wait for first data load in onGridReady
      }
    },
    [thisCpiName, buildAllColDefs, setColDefs]
  )

  // Build the chip list (and resolve human column labels) for a carried filter model. Uses the
  // manifest column list so it also labels columns hidden from the grid (e.g. ONYXIA_PRODUCT_INSTANCE).
  const buildCarriedChips = (model: Record<string, any>): ActiveFilter[] => {
    const cols = metricManifest ? generateColumns({ metricManifest }) : []
    return Object.entries(model)
      .map(([key, val]) => {
        const col = cols.find((c) => c.name === key)
        const filterName = col?.displayName || key
        if (val?.filterType === 'numberRange') {
          const label =
            val.label ||
            [typeof val.gte === 'number' ? `≥ ${val.gte}` : null, typeof val.lte === 'number' ? `≤ ${val.lte}` : null]
              .filter(Boolean)
              .join(' ')
          return label ? ({ filterName, filterValue: [label] } as ActiveFilter) : null
        }
        const values = (val?.values as string[]) || []
        if (!values.length) return null
        // Prefer the carried display label (original casing) over the lowercased match value, so a
        // query-level chip reads the same as a grid-seeded one (e.g. "OUTBOUND", not "outbound").
        return { filterName, filterValue: val.label ? [val.label] : values } as ActiveFilter
      })
      .filter((c): c is ActiveFilter => c !== null)
  }

  // Resolve a chip's display label back to the carried model key it represents (if any).
  const carriedKeyForChip = (filterName: string): string | undefined => {
    if (!carriedFilterRef.current) return undefined
    const cols = metricManifest ? generateColumns({ metricManifest }) : []
    return Object.keys(carriedFilterRef.current).find((key) => {
      const col = cols.find((c) => c.name === key)
      return key === filterName || col?.displayName === filterName
    })
  }

  useEffect(() => {
    const trendsFilters = (location.state as { trendsFilters?: Record<string, any> } | null)?.trendsFilters
    const hasTrendsFilters = !!trendsFilters && !initialFilterLoaded

    // Filters carried from the Trends tab via "View Off-SLA Entities" are applied at the QUERY level
    // (merged into the records query in getRows) and surfaced as chips, never via the AG Grid set
    // filters — see carriedFilterRef. Not persisted to localStorage, and the nav state is cleared so
    // a refresh starts clean (matches the prior behavior).
    if (hasTrendsFilters && metricManifest && trendsFilters) {
      savedFilterModel = null
      navigate(location.pathname, { replace: true })

      // After columns are built, split the carried scope per column:
      //  - values that exist in the column's lookup list go into the AG Grid set-filter model, so the
      //    header shows the funnel and the dropdown pre-checks them (native filter UI).
      //  - anything else (value not in the lookup, or a column not mounted in the grid — e.g. the
      //    hidden ONYXIA_PRODUCT_INSTANCE) stays query-level via carriedFilterRef and shows as a chip.
      // Pre-matching against the lookup is what keeps seeding safe: AG Grid silently drops a model
      // value it can't find in the (lazily-loaded) value list, so we never seed an absent value —
      // the query-level fallback carries it instead, so the data scope is never lost.
      const finishApply = async () => {
        let api = ref.current?.api
        for (let i = 0; i < 40 && !api; i++) {
          await new Promise((resolve) => setTimeout(resolve, 50))
          api = ref.current?.api
        }

        const keys = Object.keys(trendsFilters)
        // Wait until at least one target column is mounted (setInitialModel rebuilds columnDefs).
        if (api) {
          for (let i = 0; i < 40 && !keys.some((k) => api!.getColumn(k)); i++) {
            await new Promise((resolve) => setTimeout(resolve, 50))
          }
        }

        const gridModel: Record<string, any> = {}
        const carried: Record<string, any> = {}
        const matches = (a: string, b: string) => a.toLowerCase() === b.toLowerCase()

        for (const key of keys) {
          const wanted: string[] = trendsFilters[key]?.values || []
          // Only native set-filter columns can be seeded into the grid model; the EMAIL_SIZE_MB
          // number-range carry stays on the query-level path (carriedFilterRef) instead.
          const seedable = trendsFilters[key]?.filterType === 'set' && api && api.getColumn(key)
          if (!seedable) {
            carried[key] = trendsFilters[key]
            continue
          }

          let lookupValues: string[] = []
          try {
            lookupValues = (await getLookup(key)) || []
          } catch {
            lookupValues = []
          }
          /*
           * The Instance Names column offers display names, not the raw instance_name values the
           * lookup answers with, so match against the same mapping the column renders - otherwise
           * nothing matches, the whole scope falls through to the query level, and the column shows
           * no active filter over data that is filtered.
           * */
          const candidates =
            key === DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE
              ? lookupValues.map(
                  (instanceName) =>
                    dataSourceOptions.find((opt) => opt.instance_name === instanceName)?.name || instanceName
                )
              : lookupValues
          const present = wanted
            .map((v) => candidates.find((lv) => matches(lv, v)))
            .filter((v): v is string => Boolean(v))
          const absent = wanted.filter((v) => !candidates.some((lv) => matches(lv, v)))

          if (present.length) gridModel[key] = { filterType: 'set', values: present }
          // Preserve the carried label so the query-level chip keeps the canonical casing.
          if (absent.length)
            carried[key] = { filterType: 'set', values: absent, label: trendsFilters[key]?.label }
        }

        carriedFilterRef.current = Object.keys(carried).length ? carried : null
        // Query-level columns get chips here; seeded columns get theirs via handleFilterChanged.
        setActiveFilters(buildCarriedChips(carried))

        if (api && Object.keys(gridModel).length) {
          // setFilterModel applies + refetches (SSRM) and lazy-loads each set filter's values.
          api.setFilterModel(gridModel)
        } else {
          api?.refreshServerSide({ route: [], purge: true })
        }
      }

      if (!chartData) {
        const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
        const payload: TrendsDataPayload = {
          section: selectedSeverity,
          integration_ids: selectedDataSourceIds[0] === '' ? [''] : selectedDataSourceIds,
          frequency: selectedFrequency,
          timePeriod
        }
        fetchTrendsData(payload).then((res) => {
          if (res)
            setInitialModel(metricManifest, Object.keys(trendsFilters))
              .catch((e) => e)
              .finally(finishApply)
        })
      } else {
        setInitialModel(metricManifest, Object.keys(trendsFilters))
          .catch((e) => e)
          .finally(finishApply)
      }
      return
    }

    const storageFilters = localStorage.getItem('devices-filter-model')

    if (!savedFilterModel && storageFilters) {
      const parsed = JSON.parse(storageFilters)
      savedFilterModel = parsed
      if (!initialFilterLoaded && metricManifest) {
        if (!chartData) {
          const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
          const payload: TrendsDataPayload = {
            section: selectedSeverity,
            integration_ids: selectedDataSourceIds[0] === '' ? [''] : selectedDataSourceIds,
            frequency: selectedFrequency,
            timePeriod
          }

          fetchTrendsData(payload).then((res) => {
            if (res) {
              setInitialModel(metricManifest)
                .catch((e) => e)
                .finally(() => {
                  applyTrendsFilterModel(ref?.current?.api, parsed).catch((e) => e)
                })
            }
          })
        } else {
          setInitialModel(metricManifest).catch((e) => e)
        }
      }
    } else if (!initialFilterLoaded && chartData && metricManifest) {
      setInitialModel(metricManifest).catch((e) => e)
    }
  }, [chartData, initialFilterLoaded, metricManifest, dataSourceOptions])

  const onBtnExport = useCallback(() => {
    const handleExport = async () => {
      setIsDownloading(true)
      const cpiNameFormatted = formatCpiNameToDisplayName(thisCpiName)

      try {
        const timestamp = dayjs().format('YYYY_MM_DD_hh_mm_s')
        const fileName = `Onyxia_${customerName}_${cpiNameFormatted}_${timestamp}.csv`

        // Build query_filter from current table filters: the live grid set-filter model plus any
        // Trends-carried scope applied at the query level (carriedFilterRef), so the export matches
        // exactly what the table shows.
        const liveGridModel = ref.current?.api?.getFilterModel?.() || {}
        const exportModel = { ...(carriedFilterRef.current || {}), ...(savedFilterModel || {}), ...liveGridModel }
        const queryFilter = Object.keys(exportModel).length
          ? createQueryPayload(
              exportModel,
              metricManifest?.druid_spec.record_batch_spec.input_schema || [],
              dataSourceOptions
            )
          : undefined

        const response = await axios.post(
          `/api/v4/files/metrics/${cpi?.name || thisCpiName}/export/records`,
          { query_filter: queryFilter },
          {
            baseURL: process.env.REACT_APP_API_URL,
            responseType: 'blob',
            timeout: DOWNLOAD_TIMEOUT_MS,
            headers: {
              Accept: 'text/csv'
            }
          }
        )

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8'
        })

        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = fileName
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)

        toast.success(() => <HotToast text={`${cpiNameFormatted} data successfully exported.`} />)
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          const errorContent = await blobToErrorContent(error.response?.data as Blob)
          const errorMessage = errorContent?.detail || 'Data export is not yet available.'
          toast.error(() => <HotToast text={errorMessage} />)
          return
        }

        if (isAxiosError(error) && error.response?.status === 413) {
          const errorContent = await blobToErrorContent(error.response?.data as Blob)
          const errorMessage = errorContent?.detail || 'Export file is too large to download.'
          toast.error(() => <HotToast text={`${errorMessage} Please contact the support.`} />)
          return
        }

        toast.error(() => <HotToast text="An unexpected error occurred. Please try again." />)
      } finally {
        setIsDownloading(false)
      }
    }

    handleExport()
  }, [customerName, cpi, thisCpiName, dataSourceOptions, metricManifest])

  const handleResetFilter = async (colName: string) => {
    if (!ref?.current) return

    if (colName === 'all') {
      ref.current.api.setFilterModel(null)
      savedFilterModel = null
      carriedFilterRef.current = null
      localStorage?.removeItem('devices-filter-model')
      setActiveFilters([])

      ref.current.api.refreshServerSide({ route: [], purge: true })
    } else {
      // A Trends-carried scope lives in carriedFilterRef (query-level), not in the grid filter
      // model, so handle its chip removal here before the grid-filter path.
      const carriedKey = carriedKeyForChip(colName)
      if (carriedKey) {
        const next = { ...carriedFilterRef.current }
        delete next[carriedKey]
        carriedFilterRef.current = Object.keys(next).length ? next : null
        setActiveFilters([...activeFilters].filter((c) => c.filterName !== colName))
        ref.current.api.refreshServerSide({ route: [], purge: true })
        return
      }

      // Find column definition by headerName first, then fall back to field or colId
      const thisColDef = colDefs.find(
        (c) => c.headerName === colName || c.field === colName || (c as any).colId === colName
      ) as ColDef | undefined

      if (thisColDef) {
        // Use colId if available (matches how BooleanFilter creates keys), otherwise use field
        const columnKey = (thisColDef as any).colId || thisColDef.field

        if (columnKey) {
          // Get the current filter model directly from the grid API instead of savedFilterModel
          // This ensures we have the latest state, including filters applied by BooleanFilter
          const currentFilterModel = ref.current.api.getFilterModel() || {}
          const newFilterModel = JSON.parse(JSON.stringify(currentFilterModel))
          delete newFilterModel[columnKey]

          // Only remove the specific filter from activeFilters
          const newActiveFilters = [...activeFilters].filter((c) => c.filterName !== colName)

          setActiveFilters(newActiveFilters)
          savedFilterModel = newFilterModel
          localStorage.setItem('devices-filter-model', JSON.stringify(newFilterModel))
          ref.current.api.setFilterModel(newFilterModel)
          ref.current.api.refreshServerSide({ route: [], purge: true })
        }
      }
    }
  }

  const onGridReady = useCallback(
    async (params: GridReadyEvent) => {
      const dataSource = {
        getRows: async (params: any) => {
          setGridApi(params.api)

          const cacheKey = `${params.request.startRow}-${params.request.endRow}`

          try {
            const page = Math.round(params.request.endRow / 100) < 1 ? 1 : params.request.endRow / 100
            const gridState = params.api.getState()

            let payload
            let unfiltered = false
            try {
              // Merge any Trends-carried scope (applied at the query level, see carriedFilterRef) with
              // the live grid filter model. The grid model wins per-column so the user can still refine.
              const gridFilterModel = gridState?.filter?.filterModel || {}
              const mergedModel = { ...(carriedFilterRef.current || {}), ...gridFilterModel }
              unfiltered = Object.keys(mergedModel).length === 0
              payload = createQueryPayload(
                Object.keys(mergedModel).length ? mergedModel : null,
                metricManifest?.druid_spec.record_batch_spec.input_schema || [],
                dataSourceOptions
              )
            } catch (payloadError) {
              params.fail()
              return
            }

            const result = await fetchQuery({ page, payload })

            if (!result || !result.success) {
              console.error('API call failed:', result?.error || 'No result')

              const filterError = checkForFilterLimitError(result?.error)
              if (filterError) {
                if (isHandlingError) {
                  params.success({ rowData: [], rowCount: 0 })
                  return
                }

                setIsHandlingError(true)
                setFilterLimitError(filterError)

                // Don't modify savedFilterModel here - just return empty data
                params.success({ rowData: [], rowCount: 0 })
                dispatch(setDevicesCount(0))
                return
              }

              setShowErrorOverlay(true)
              params.success({ rowData: [], rowCount: 0 })
              dispatch(setDevicesCount(0))
              return
            }

            // Success path - clear any error handling flags
            setShowErrorOverlay(false)
            setFilterLimitError(null)
            setIsHandlingError(false)

            const res = result.response
            const data = res?.data?.data || []
            const totalResults = res?.data?.pagination?.total_results || 0

            dispatch(setDevicesCount(totalResults))
            if (unfiltered) setHasRecords(totalResults > 0)

            if (data.length === 0 && totalResults === 0) {
              params.api.showNoRowsOverlay()
            } else {
              params.api.hideOverlay()
            }

            const reworkedData = withRowIds<DevicesDataItem>(
              data.map((item: DevicesDataItem) => formatDateValues(item, metricManifest)),
              rowIdFieldRef.current,
              params.request.startRow ?? 0
            )

            setCachedData((prev) => {
              const newCache = new Map(prev)
              newCache.set(cacheKey, reworkedData)
              return newCache
            })

            // Update localStorage with successful filter
            localStorage.setItem('devices-filter-model', JSON.stringify(savedFilterModel))

            params.success({
              rowData: reworkedData,
              rowCount: totalResults
            })
          } catch (err) {
            console.error('Caught error in getRows:', err)

            const filterError = checkForFilterLimitError(err)
            if (filterError) {
              if (isHandlingError) {
                params.success({ rowData: [], rowCount: 0 })
              } else {
                setIsHandlingError(true)
                setFilterLimitError(filterError)
                params.success({ rowData: [], rowCount: 0 })
                dispatch(setDevicesCount(0))
              }
            } else {
              setShowErrorOverlay(true)
              dispatch(setDevicesCount(0))
              params.fail()
            }
          } finally {
            // Always clear loading flags so the header skeletons never get stuck,
            // regardless of which path getRows took.
            setIsFilterLoading(false)
            setInitialFilterLoaded(true)
            setColumnsFetched(true)
          }
        }
      }

      params.api.setGridOption('serverSideDatasource', dataSource)
    },
    [
      savedFilterModel,
      fetchQuery,
      dispatch,
      cachedData,
      snowflakeManifest,
      dataSourceOptions,
      isHandlingError,
      initialFilterLoaded
    ]
  )

  useEffect(() => {
    if (gridApi && savedFilterModel !== null) {
      gridApi.refreshServerSide({ route: [], purge: true })
    }
  }, [savedFilterModel, gridApi])

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      suppressHeaderMenuButton: true
    }
  }, [])

  const handleFilterChanged = useCallback(
    async (e: FilterChangedEvent<DevicesDataItem, any>) => {
      if (!ref.current) return

      // Block all filter changes while handling an error
      if (isHandlingError) {
        console.info('Blocking filter change - currently handling error')
        return
      }

      if (e.source === 'columnFilter' || e.afterFloatingFilter) {
        ref.current.api.hideOverlay()
        ref.current.api.setRowCount(100)

        const currentFilterModel = e.api.getState()?.filter?.filterModel || null

        // Save the current valid state BEFORE applying the new filter
        setLastValidActiveFilters([...activeFilters])
        setLastValidFilterModel(savedFilterModel ? { ...savedFilterModel } : null)

        savedFilterModel = currentFilterModel
        // Don't show loading overlay when filter popup is open to prevent it from covering/hiding the popup
        if (!isFilterPopupOpen.current) {
          setIsFilterLoading(true)
          setColumnsFetched(false) // Show loading skeleton in tab when filter changes
        }
        ref.current.api.refreshServerSide({ route: [], purge: true })
      }

      const timeoutId = setTimeout(() => {
        if (!ref.current || isHandlingError) return

        const state = e.api.getState()
        // Trends-carried scope (query-level) isn't in the grid filter model, so re-add its chips for
        // any column the user hasn't overridden with a grid filter — otherwise they'd vanish on the
        // first grid-filter interaction even though the scope is still applied to the query.
        const gridModel = state?.filter?.filterModel || {}
        const carriedChips = carriedFilterRef.current
          ? buildCarriedChips(carriedFilterRef.current).filter((chip) => {
              const key = carriedKeyForChip(chip.filterName)
              return !key || !gridModel[key]
            })
          : []
        if (state && state.filter && state.filter.filterModel) {
          const newActiveFilters: ActiveFilter[] = [...carriedChips]

          for (const [key, value] of Object.entries(state.filter.filterModel)) {
            // Match by field or colId (BooleanFilter uses getColId() which may differ from field)
            const colDef = colDefs.find((c) => c.field === key || (c as any).colId === key) as ColDef | undefined
            const filterName = colDef?.headerName || key

            const columnConfig = columns.find((col) => col.name === key)
            const isDateColumn = isDateRangeColumn(columnConfig?.filterPredicates)

            if (value && value.filterType === 'boolean') {
              if (value.value) {
                newActiveFilters.push({
                  filterName,
                  filterValue: [value.value === 'true' ? 'true' : 'false']
                })
              }
            } else if (isDateColumn && value) {
              newActiveFilters.push({
                filterName,
                filterValue: value
              })
            } else {
              if (value && value.values && value.values.length > 0) {
                newActiveFilters.push({
                  filterName,
                  filterValue: value.values
                })
              }
            }
          }

          setActiveFilters(newActiveFilters)
        } else {
          setActiveFilters(carriedChips)
        }
      }, 300)

      return () => clearTimeout(timeoutId)
    },
    [activeFilters, gridApi, colDefs, columns, isHandlingError, metricManifest]
  )

  const handleFilterErrorClose = useCallback(() => {
    setFilterLimitError(null)

    // Use the saved last valid filter model (saved before the failed filter was applied)
    const previousModel = lastValidFilterModel

    // Update savedFilterModel to the last valid state
    savedFilterModel = previousModel

    // Restore the active filters display
    setActiveFilters(lastValidActiveFilters)

    if (ref.current?.api) {
      // Silently set the filter model back without triggering change events
      ref.current.api.setFilterModel(previousModel)

      // Wait a bit then clear the error handling flag and refresh
      setTimeout(() => {
        setIsHandlingError(false)

        // Refresh the grid with the reverted filter
        if (ref.current?.api) {
          ref.current.api.refreshServerSide({ route: [], purge: true })
        }
      }, 200)
    } else {
      setIsHandlingError(false)
    }
  }, [lastValidActiveFilters, lastValidFilterModel])

  useEffect(() => {
    if (gridApi) {
      const handleFilterClosed = async (event: any) => {
        isFilterPopupOpen.current = false
        // Clear the mini filter immediately when filter closes (no delay)
        // This ensures AG Grid's internal state is updated before the popup is destroyed
        try {
          const filterInstance = await gridApi.getColumnFilterInstance(event.column.colId)
          if (filterInstance && typeof filterInstance.setMiniFilter === 'function') {
            filterInstance.setMiniFilter('')
          }
        } catch (error) {
          console.error('Error clearing mini filter:', error)
        }
      }

      gridApi.addEventListener('filterClosed', handleFilterClosed)

      return () => {
        gridApi.removeEventListener('filterClosed', handleFilterClosed)
        setInitialQueryLoaded(false)
      }
    }
  }, [gridApi])

  useEffect(() => {
    setCachedData(new Map())
  }, [savedFilterModel])

  const components = useMemo(
    () => ({
      agDateInput: DTPicker,
      booleanFilter: BooleanFilter
    }),
    []
  )

  const handleRestoreDefaultColumns = useCallback(async () => {
    const defaults = metricManifest ? generateColumns({ metricManifest }) : [...columns]

    if (metricManifest) setColumns(defaults)

    await restoreDefaultColumns(defaults)
  }, [columns, metricManifest, setColumns, restoreDefaultColumns])

  const loadingCellRenderer = useCallback(LoadingRow, [])
  const loadingCellRendererParams = useMemo(() => {
    return {
      loadingMessage: 'One moment please...'
    }
  }, [])

  /*
   * ONYXIA-6339: Close filter popup on zoom detected
   * */
  useEffect(() => {
    if (!gridApi) return

    let lastZoom = window.devicePixelRatio

    const handleZoomChange = () => {
      const currentZoom = window.devicePixelRatio

      if (currentZoom !== lastZoom) {
        try {
          const allColumns = gridApi.getColumns()
          if (allColumns) {
            allColumns.forEach((column: any) => {
              try {
                const filterInstance = gridApi.getColumnFilterInstance(column.getColId())
                if (filterInstance && typeof filterInstance.hideFilterMenu === 'function') {
                  filterInstance.hideFilterMenu()
                }
              } catch (error) {
                // Ignore errors for columns without filters
              }
            })
          }

          const openPopups = document.querySelectorAll('.ag-popup')
          openPopups.forEach((popup) => {
            if (popup instanceof HTMLElement) {
              popup.style.display = 'none'
            }
          })

          const dtPickerCalendars = document.querySelectorAll('.datepicker-filter-popper, .react-datepicker-popper')
          dtPickerCalendars.forEach((calendar) => {
            if (calendar instanceof HTMLElement) {
              calendar.style.display = 'none'
              calendar.style.visibility = 'hidden'
              calendar.style.opacity = '0'
              calendar.style.pointerEvents = 'none'
            }
          })

          const reactDatePickers = document.querySelectorAll('.react-datepicker')
          reactDatePickers.forEach((picker) => {
            if (picker instanceof HTMLElement) {
              picker.style.display = 'none'
              picker.style.visibility = 'hidden'
            }
          })

          const portalElements = document.querySelectorAll('#root .react-datepicker-popper, #root .react-datepicker')
          portalElements.forEach((element) => {
            if (element instanceof HTMLElement) {
              element.style.display = 'none'
            }
          })

          const escapeEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true
          })

          const dateInputs = document.querySelectorAll('input[placeholder*="dd-MM-yyyy"]')
          dateInputs.forEach((input) => {
            if (input instanceof HTMLElement) {
              input.dispatchEvent(escapeEvent)
              input.blur() // Also blur the input
            }
          })

          document.body.dispatchEvent(escapeEvent)

          const clickOutsideEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: 0,
            clientY: 0
          })

          // Click on a safe area to trigger onClickOutside handlers
          setTimeout(() => {
            document.body.dispatchEvent(clickOutsideEvent)
          }, 10)
        } catch (error) {
          console.warn('Error closing filter dropdowns and calendars:', error)
        }

        lastZoom = currentZoom
      }
    }

    const handleResize = () => {
      setTimeout(handleZoomChange, 50)
    }

    const handleVisualViewportChange = () => {
      handleZoomChange()
    }

    window.addEventListener('resize', handleResize)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange)
      }
    }
  }, [gridApi])

  // Re-checked on every filter change so "select all" is disabled before the user clicks.
  useEffect(() => {
    const model = { ...(carriedFilterRef.current || {}), ...(ref.current?.api?.getFilterModel?.() || {}) }
    if (!Object.keys(model).length) {
      setFilterRule({ conditions: [], blocked: NO_FILTER_MESSAGE })
      return
    }
    if (!exclusionFields) {
      setFilterRule({ conditions: [], blocked: EXCLUSION_FIELDS_UNAVAILABLE_MESSAGE })
      return
    }
    const inputSchema = metricManifest?.druid_spec.record_batch_spec.input_schema || []
    const queryFilter = createQueryPayload(model, inputSchema, dataSourceOptions) as Record<
      string,
      Record<string, unknown>
    >
    const { conditions, unsupportedColumns } = convertFilterToConditions(queryFilter, exclusionFields.fields)
    if (unsupportedColumns.length) {
      const names = unsupportedColumns.map((col) => inputSchema.find((c) => c.name === col)?.displayName || col)
      setFilterRule({ conditions: [], blocked: unsupportedFieldsMessage(names) })
      return
    }
    setFilterRule(conditions.length ? { conditions, blocked: null } : { conditions: [], blocked: NO_FILTER_MESSAGE })
  }, [activeFilters, exclusionFields, metricManifest, dataSourceOptions])

  const handleRulesCountChange = useCallback((count: number) => setRulesCount(count), [])

  const handleExclusionRulesBack = useCallback(() => setView('devices'), [])

  // The rules view unmounts the grid and its filter model, so start the records view clean on return.
  const openRulesView = useCallback(() => {
    savedFilterModel = null
    carriedFilterRef.current = null
    localStorage.removeItem('devices-filter-model')
    setActiveFilters([])
    setLastValidActiveFilters([])
    setLastValidFilterModel(null)
    setSelectedIds([])
    setSelectAll({ active: false, deselected: 0 })
    setView('rules')
  }, [])

  if (view === 'rules') {
    return (
      <ExclusionRulesView
        cpiId={cpi?.name || thisCpiName}
        onBack={handleExclusionRulesBack}
        onRulesCountChange={handleRulesCountChange}
        onCreateRule={(onCreated) => openCreateRuleModal('empty', [], undefined, onCreated)}
        createBlockedReason={hasRecords === false ? NO_RECORDS_TO_EXCLUDE_MESSAGE : null}
        onEditRule={openEditRuleModal}
      />
    )
  }

  const selectedCount = selectAll.active
    ? Math.max(devicesPagination.total_results - selectAll.deselected, 0)
    : selectedIds.length
  const createRuleBlockedReason = !canEditExclusionRules
    ? NO_EDIT_PERMISSION_MESSAGE
    : selectAll.active
    ? selectAll.deselected > 0
      ? PARTIAL_SELECT_ALL_MESSAGE
      : filterRule.blocked
    : null

  const createTicketBlockedReason = !canCreateTickets ? (
    TICKET_NO_PERMISSION_MESSAGE
  ) : noTicketingIntegration ? (
    noTicketingIntegrationMessage
  ) : selectedCount > MAX_TICKET_RECORDS ? (
    TICKET_LIMIT_MESSAGE
  ) : null

  const noExclusionRules = rulesCount === 0
  const exclusionRulesTooltip = noExclusionRules
    ? 'Records matching exclusion rules are ignored in calculations.\nNo exclusion rules are currently applied.'
    : 'Records matching these rules are excluded from selected metrics.'

  return (
    <>
      <DevicesTableContainer className="devices-table-container">
        <ManagementStatusWrapper className="management-status-wrapper">
          <FilterChipContainer
            activeFilters={activeFilters.map((filter) => ({
              ...filter,
              filterValue: getDisplayFilterValue(filter, dataSourceOptions)
            }))}
            handleResetFilter={handleResetFilter}
          />

          <ClickAwayListener
            onClickAway={() => {
              setColumnsPopperOpen(false)
              setPopperAnchorEl(null)
            }}
          >
            <Box className="controls-container">
              <OnyxiaTooltip
                title={exclusionRulesTooltip}
                placement="bottom"
                componentsProps={{
                  tooltip: {
                    sx: {
                      width: noExclusionRules ? '275px' : '240px',
                      height: noExclusionRules ? '75px' : '58px',
                      boxSizing: 'border-box',
                      padding: '8px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      whiteSpace: 'pre-line',
                      textAlign: 'left'
                    }
                  }
                }}
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <ExclusionRulesButton
                    role="button"
                    tabIndex={0}
                    onClick={openRulesView}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') openRulesView()
                    }}
                  >
                    Exclusion Rules
                  </ExclusionRulesButton>
                </Box>
              </OnyxiaTooltip>
              <LibraryDetailsHoverTooltip title="Export CSV">
                <div>
                  <ControlButton
                    type="export"
                    setAnchorEl={setPopperAnchorEl}
                    loading={isDownloading}
                    handleClick={onBtnExport}
                  />
                </div>
              </LibraryDetailsHoverTooltip>
              <LibraryDetailsHoverTooltip title="Manage Columns" placement="bottom-start">
                <div>
                  <ControlButton
                    type="columns"
                    setAnchorEl={setPopperAnchorEl}
                    handleClick={() => setColumnsPopperOpen((pre) => !pre)}
                    open={columnsPopperOpen}
                  />
                </div>
              </LibraryDetailsHoverTooltip>
              <ColumnPicker
                anchorEl={popperAnchorEl}
                open={columnsPopperOpen}
                columns={columns as any}
                handleChangeColumns={handleChangeColumns}
                handleRestoreDefaultColumns={handleRestoreDefaultColumns}
                isUpdating={isUpdatingColumns}
              />
            </Box>
          </ClickAwayListener>
        </ManagementStatusWrapper>

        <HeaderInfoWrapper>
          <Typography>
            Number of rows:{' '}
            {!initialFilterLoaded || isFilterLoading ? (
              <Skeleton
                variant="rectangular"
                width={50}
                height={16}
                animation="wave"
                sx={{
                  display: 'inline-block',
                  bgcolor: 'rgba(87, 82, 86, 0.5)',
                  borderRadius: '4px',
                  verticalAlign: 'middle',
                  '&::after': {
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)'
                  }
                }}
              />
            ) : (
              <span>{devicesPagination.total_results}</span>
            )}
          </Typography>
          <Typography>
            Last Update:{' '}
            {!initialFilterLoaded || isFilterLoading ? (
              <Skeleton
                variant="rectangular"
                width={120}
                height={16}
                animation="wave"
                sx={{
                  display: 'inline-block',
                  bgcolor: 'rgba(87, 82, 86, 0.5)',
                  borderRadius: '4px',
                  verticalAlign: 'middle',
                  '&::after': {
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent)'
                  }
                }}
              />
            ) : (
              <span>{dayjs(defaultRecentValueDate.date).format('ddd, MMM DD, YYYY')}</span>
            )}
          </Typography>
        </HeaderInfoWrapper>

        {snowflakeManifest ? (
          <Box sx={{ position: 'relative', height: '100%' }}>
            {isFilterLoading && <FilterLoadingOverlay />}
            <AgGridReact<DevicesDataItem>
              ref={ref}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              components={components}
              onRowClicked={(row) => {
                const target = row.event?.target as HTMLElement
                if (target) {
                  const isActionsColumn =
                    target.closest('.ag-pinned-right-cols-container') !== null ||
                    target.closest('[col-id="actions"]') !== null

                  if (isActionsColumn) {
                    return // Don't open drawer if clicking on actions column
                  }
                }

                setDevicesDataDrawerOpen(!devicesDataDrawerOpen)
                setActiveRowItem(row.data || null)
              }}
              rowHeight={64}
              rowStyle={{ maxHeight: '60px' }}
              headerHeight={49}
              rowModelType="serverSide"
              rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true } as any}
              getRowId={(params) => (params.data as any)?.[ROW_ID_KEY] ?? ''}
              onSelectionChanged={(event) => {
                // Source selection from AG Grid's SSRM state — it tracks IDs across cache
                // purges, so rows selected before a filter change survive the reload even
                // when they're no longer in the loaded block.
                const state = (event.api as any).getServerSideSelectionState?.() as
                  | { selectAll: boolean; toggledNodes: string[] }
                  | null
                  | undefined

                setSelectAll({
                  active: Boolean(state?.selectAll),
                  deselected: state?.selectAll && Array.isArray(state.toggledNodes) ? state.toggledNodes.length : 0
                })

                if (state && Array.isArray(state.toggledNodes) && !state.selectAll) {
                  setSelectedIds(state.toggledNodes)
                  return
                }

                // Fallback for the virtual "select all" state (or older API): collect
                // selected IDs from currently loaded nodes.
                const ids: string[] = []
                event.api.forEachNode((node) => {
                  if (node.isSelected() && node.data) {
                    const id = (node.data as any)[ROW_ID_KEY]
                    if (id != null) ids.push(id)
                  }
                })
                setSelectedIds(ids)
              }}
              onGridReady={onGridReady}
              onFilterChanged={handleFilterChanged}
              onFilterOpened={async (e) => {
                isFilterPopupOpen.current = true
                // Clear the mini filter search when filter is opened
                // Use setTimeout to ensure the filter popup is fully rendered first
                setTimeout(() => {
                  const miniFilterInput = document.querySelector('.ag-mini-filter input') as HTMLInputElement
                  if (miniFilterInput && miniFilterInput.value) {
                    miniFilterInput.value = ''
                    // Dispatch input event to trigger AG Grid to re-render filter options without highlighting
                    miniFilterInput.dispatchEvent(new Event('input', { bubbles: true }))
                  }
                }, 0)
                // The set filter value list is hidden by CSS until handleFilterState() adds the
                // 'filter-has-values' class, which only runs while the popup is open. When a filter
                // model is applied programmatically (carried from Trends), the values load while the
                // popup is closed, so the class never gets added and the list stays blank. Re-load the
                // values now that the popup is open so handleFilterState() runs and reveals the list.
                try {
                  const colId = (e.column as any)?.getColId?.()
                  if (colId) {
                    const inst: any = await e.api.getColumnFilterInstance(colId)
                    inst?.refreshFilterValues?.()
                  }
                } catch (err) {
                  /* non-set-filter columns have no refreshFilterValues; ignore */
                }
              }}
              onColumnVisible={(event) => {
                // Update columns state when a column is hidden (e.g., dragged outside)
                if (event.column && !event.visible) {
                  const colId = event.column.getColId()
                  setColumns((prev) => prev.map((c) => (c.name === colId ? { ...c, active: false } : c)))
                }
              }}
              loading={false}
              loadingCellRenderer={loadingCellRenderer}
              loadingCellRendererParams={loadingCellRendererParams}
              suppressRowVirtualisation={false}
              suppressServerSideFullWidthLoadingRow={true}
              cacheBlockSize={100}
              maxBlocksInCache={5}
              serverSideInitialRowCount={100}
              rowBuffer={10}
              maxConcurrentDatasourceRequests={1}
              blockLoadDebounceMillis={200}
              suppressNoRowsOverlay={false}
              noRowsOverlayComponent={() =>
                showErrorOverlay ? (
                  <ErrorOverlay />
                ) : (
                  // "blank" (the bare "No data for this CPI" copy) only when no filter of any kind is
                  // applied. A carried Trends scope lives on carriedFilterRef at the query level (not the
                  // grid model, so savedFilterModel is null), so check it and the chips too — otherwise an
                  // empty filtered result wrongly reads as "no data".
                  <NoResultsFound blank={!savedFilterModel && !carriedFilterRef.current && activeFilters.length === 0} />
                )
              }
              tooltipShowDelay={200}
              animateRows={false}
              suppressScrollOnNewData={true}
              suppressContextMenu={true}
              postProcessPopup={positionRecordsGridPopup}
            />
          </Box>
        ) : (
          <div>Loading...</div>
        )}

        {(selectedIds.length > 0 || selectAll.active) && (
          <SelectionActionBar>
            <Typography className="selection-count">
              <span className="selection-count__label">Selected:</span>{' '}
              <span className="selection-count__value">
                {selectedCount} {selectedCount === 1 ? 'record' : 'records'}
              </span>
            </Typography>
            <Box className="selection-actions">
              {createRuleBlockedReason && canEditExclusionRules && (
                <Typography className="selection-message">{createRuleBlockedReason}</Typography>
              )}
              <OnyxiaTooltip title={createTicketBlockedReason ?? ''} placement="top" arrow={false}>
                <span>
                  <ThemeButton
                    className="create-ticket-cta"
                    themevariant="secondary"
                    shape="rectangular"
                    disabled={Boolean(createTicketBlockedReason) || ticketingApplications === null}
                    onClick={handleCreateTicketFromSelection}
                  >
                    Create Ticket
                  </ThemeButton>
                </span>
              </OnyxiaTooltip>
              <OnyxiaTooltip
                title={canEditExclusionRules ? '' : NO_EDIT_PERMISSION_MESSAGE}
                placement="top-end"
                arrow={false}
              >
                <span>
                  <ThemeButton
                    className="exclusion-rule-cta"
                    themevariant="primary"
                    shape="rectangular"
                    disabled={Boolean(createRuleBlockedReason)}
                    onClick={handleCreateRuleFromSelection}
                  >
                    Create Exclusion Rule
                  </ThemeButton>
                </span>
              </OnyxiaTooltip>
            </Box>
          </SelectionActionBar>
        )}
      </DevicesTableContainer>

      <DataDrawer
        manifest={metricManifest}
        drawerOpen={devicesDataDrawerOpen}
        setDrawerOpen={setDevicesDataDrawerOpen}
        activeRowItem={activeRowItem}
        snowflakeManifest={snowflakeManifest}
        dataSourceOptions={dataSourceOptions}
      />

      {filterLimitError && (
        <FilterLimitErrorModal
          open={!!filterLimitError}
          onClose={handleFilterErrorClose}
          columnName={filterLimitError.context.column_name}
          actualCount={filterLimitError.context.actual_count}
          limit={filterLimitError.context.limit}
        />
      )}
    </>
  )
}

export default Devices
